import { useState } from "react";
import { SurvivalAt, type SurvivalStep } from "../../../../lib/sampleStats";
import { Axes, ChartSvg, LinearScale, M, NiceTicks, PointerToViewBoxX, SERIES, Tooltip, W, H, type TooltipState } from "./ChartKit";

function StepPath(steps: SurvivalStep[], maxTime: number, x: (v: number) => number, y: (v: number) => number): string {
  let d = `M${x(0)} ${y(1)}`;
  for (const s of steps.slice(1)) d += ` H${x(Math.min(s.time, maxTime))} V${y(s.survival)}`;
  return `${d} H${x(maxTime)}`;
}

const percent = (v: number) => `${Math.round(v * 100)}%`;

export function SurvivalChart({ curves, maxTime }: { curves: [SurvivalStep[], SurvivalStep[]]; maxTime: number }) {
  const [time, setTime] = useState<number | null>(null);
  const x = LinearScale([0, maxTime], [M.left, W - M.right]);
  const y = LinearScale([0, 1], [H - M.bottom, M.top]);
  const toTime = LinearScale([M.left, W - M.right], [0, maxTime]);
  const tip: TooltipState | null = time === null ? null : {
    x: x(time), y: M.top + 40,
    lines: [`Month ${time.toFixed(1)}`, ...curves.map((c, i) => `${SERIES[i].name}: ${percent(SurvivalAt(c, time))} surviving`)],
  };
  return (
    <div className="relative">
      <ChartSvg label="Kaplan–Meier survival curves for Group A and Group B" onLeave={() => setTime(null)}>
        <Axes xTicks={NiceTicks(0, maxTime, 6)} yTicks={[0, 0.25, 0.5, 0.75, 1]} x={x} y={y} xLabel="Months" yLabel="Survival probability" yFormat={percent} />
        <line x1={M.left} x2={W - M.right} y1={y(0.5)} y2={y(0.5)} strokeDasharray="4 4" className="stroke-[#0A0A0F]/30 dark:stroke-white/30" />
        {curves.map((c, i) => (
          <path key={SERIES[i].name} d={StepPath(c, maxTime, x, y)} fill="none" strokeWidth={2} strokeLinejoin="round" className={SERIES[i].stroke} />
        ))}
        {time !== null && (
          <g pointerEvents="none">
            <line x1={x(time)} x2={x(time)} y1={M.top} y2={H - M.bottom} className="stroke-[#0A0A0F]/40 dark:stroke-white/40" />
            {curves.map((c, i) => <circle key={i} cx={x(time)} cy={y(SurvivalAt(c, time))} r={4} strokeWidth={2} className={`${SERIES[i].fill} stroke-white dark:stroke-[#0D0D10]`} />)}
          </g>
        )}
        <rect
          x={M.left} y={M.top} width={W - M.left - M.right} height={H - M.top - M.bottom} fill="transparent"
          onMouseMove={(e) => setTime(Math.max(0, Math.min(maxTime, toTime(PointerToViewBoxX(e)))))}
        />
      </ChartSvg>
      <Tooltip tip={tip} />
    </div>
  );
}
